import React, { useEffect, useMemo, useState } from "react";
import ModalCloseBtn from "@/components/ModalCloseBtn";
import { Calendar, Plus, Trash2, Check, X, Image as ImageIcon, StickyNote, Link as LinkIcon, ExternalLink, Sparkles, BarChart3, Shield, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import Layout, { PageHeader, StatCard } from "@/components/Layout";
import api, { BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { getNav } from "@/pages/navConfigs";
import { useSubmitting } from "@/lib/useSubmitting";

const PLATFORMS = ["instagram", "twitter", "linkedin", "facebook"];

export default function SMDashboard() {
  const { user, customRole } = useAuth();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [posts, setPosts] = useState([]);
  const [monthOffset, setMonthOffset] = useState(0);
  const [projects, setProjects] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [form, setForm] = useState({ title: "", content_idea: "", description: "", platform: "instagram", image_url: "", custom_fields: [] });
  const [openPost, setOpenPost] = useState(null);
  const [notes, setNotes] = useState([]);
  const [links, setLinks] = useState([]);
  const [view, setView] = useState("calendar"); // calendar | notes | links

  useEffect(() => {
    (async () => {
      const [e, n, l, p] = await Promise.all([
        api.get("/events"),
        api.get("/sm/notes"),
        api.get("/sm/links"),
        api.get("/sm-projects").catch(() => ({ data: [] })),
      ]);
      setEvents(e.data); setNotes(n.data); setLinks(l.data); setProjects(p.data);
      // Auto-select first available — event preferred, project as fallback
      const first = e.data[0]
        ? { event_id: e.data[0].event_id, name: e.data[0].name, kind: "event", source: e.data[0] }
        : (p.data[0] ? { event_id: p.data[0].project_id, name: p.data[0].name, kind: "project", source: p.data[0] } : null);
      if (first) setSelectedEvent(first);
    })();
  }, []);

  useEffect(() => {
    if (!selectedEvent) { setPosts([]); return; }
    // Clear immediately so the calendar doesn't briefly show the previous event's posts
    setPosts([]);
    setSelectedDate(null);
    let cancelled = false;
    (async () => {
      const r = await api.get(`/events/${selectedEvent.event_id}/social-posts`);
      if (!cancelled) setPosts(r.data);
    })();
    return () => { cancelled = true; };
  }, [selectedEvent?.event_id, selectedEvent?.kind]);

  // Refresh projects list when user returns to the calendar tab so any
  // newly-created External Project shows up in the selector immediately.
  useEffect(() => {
    if (view !== "calendar") return;
    (async () => {
      try { setProjects((await api.get("/sm-projects")).data || []); } catch { /* ignore */ }
    })();
  }, [view]);

  const now = new Date();
  const monthDate = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
  const monthName = monthDate.toLocaleString("default", { month: "long", year: "numeric" });
  const startWeekday = monthDate.getDay();
  const daysInMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0).getDate();

  // Compute allowed window for this event:
  // From event.created_at (or start_date if project) → event.end_date + 5 days.
  const windowRange = useMemo(() => {
    const src = selectedEvent?.source;
    if (!src) return null;
    const startIso = (src.created_at || src.start_date || "").slice(0, 10);
    const endBase = src.end_date || src.start_date;
    if (!startIso || !endBase) return null;
    // Parse `endBase` as YYYY-MM-DD in local time (Date's default treats it as UTC)
    // and add 5 days using local getDate/setDate — then format the result manually.
    const [ey, em, ed] = endBase.slice(0, 10).split("-").map(Number);
    const endDt = new Date(ey, (em || 1) - 1, ed || 1);
    endDt.setDate(endDt.getDate() + 5);
    const endStr = `${endDt.getFullYear()}-${String(endDt.getMonth() + 1).padStart(2, "0")}-${String(endDt.getDate()).padStart(2, "0")}`;
    return { start: startIso, end: endStr };
  }, [selectedEvent]);

  const cells = useMemo(() => {
    const arr = [];
    for (let i = 0; i < startWeekday; i++) arr.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      // Use LOCAL date components — toISOString() shifts to UTC which puts
      // IST users a day behind (00:00 IST = 18:30 UTC previous day).
      const y = monthDate.getFullYear();
      const m = monthDate.getMonth() + 1;
      const dateStr = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      const inWindow = !windowRange || (dateStr >= windowRange.start && dateStr <= windowRange.end);
      arr.push({ date: dateStr, day: d, inWindow, posts: posts.filter((p) => p.scheduled_date === dateStr) });
    }
    return arr;
  }, [monthDate, daysInMonth, startWeekday, posts, windowRange]);

  const addPost = async (e) => {
    e.preventDefault();
    if (!selectedEvent || !selectedDate) return;
    if (windowRange && (selectedDate < windowRange.start || selectedDate > windowRange.end)) {
      toast.error("This date is outside the event's active window (creation → 5 days after event end).");
      return;
    }
    const currentEventId = selectedEvent.event_id;
    const scheduledDate = selectedDate;
    try {
      const created = await api.post(`/events/${currentEventId}/social-posts`, { ...form, scheduled_date: scheduledDate });
      toast.success(`Post scheduled for ${scheduledDate}`);
      setForm({ title: "", content_idea: "", description: "", platform: "instagram", image_url: "", custom_fields: [] });
      // Optimistic insert so the user sees it immediately even if the refetch races.
      setPosts((prev) => (prev.some((p) => p.post_id === created.data.post_id) ? prev : [...prev, created.data]));
      // Follow-up fetch to sync any server-side normalisation.
      const r = await api.get(`/events/${currentEventId}/social-posts`);
      // Only apply if the user hasn't switched events while the request was in flight.
      if (currentEventId === (selectedEvent?.event_id || currentEventId)) setPosts(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed — try again");
    }
  };

  const togglePosted = async (p) => { await api.patch(`/social-posts/${p.post_id}`, { posted: !p.posted }); const r = await api.get(`/events/${selectedEvent.event_id}/social-posts`); setPosts(r.data); };
  const delPost = async (p) => { await api.delete(`/social-posts/${p.post_id}`); const r = await api.get(`/events/${selectedEvent.event_id}/social-posts`); setPosts(r.data); };

  // Reschedule an existing post to a new date. Any user who can see this dashboard
  // (SM Manager / Executive / Admin / custom role with `calendar` capability) may
  // shift the post to any date within the event's active window — earlier or later.
  const movePost = async (p, newDate) => {
    if (!newDate || newDate === p.scheduled_date) return;
    if (windowRange && (newDate < windowRange.start || newDate > windowRange.end)) {
      toast.error("New date is outside the event's active window (creation → 5 days after event end).");
      return;
    }
    try {
      await api.patch(`/social-posts/${p.post_id}`, { scheduled_date: newDate });
      const r = await api.get(`/events/${selectedEvent.event_id}/social-posts`);
      setPosts(r.data);
      setOpenPost({ ...p, scheduled_date: newDate });
      setSelectedDate(newDate);
      toast.success(`Moved to ${newDate}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Move failed — try again");
    }
  };

  const dayPosts = selectedDate ? posts.filter((p) => p.scheduled_date === selectedDate) : [];

  return (
    <Layout navItems={getNav(user.role, customRole)}>
      <div className="p-6 lg:p-10 max-w-7xl mx-auto">
        <PageHeader title={`Hey, ${user.name.split(" ")[0]} ✨`} subtitle={`Social Media${(events.length + projects.length) === 0 ? " — Start by creating an External Project" : ` — ${events.length} event${events.length === 1 ? "" : "s"}, ${projects.length} project${projects.length === 1 ? "" : "s"}`}`} icon={Sparkles} accent="pink"
          actions={
            <select className="input-pill py-2" value={selectedEvent?.event_id || ""} onChange={(e) => {
              const id = e.target.value;
              const ev = events.find((x) => x.event_id === id);
              if (ev) { setSelectedEvent({ event_id: ev.event_id, name: ev.name, kind: "event", source: ev }); return; }
              const pr = projects.find((x) => x.project_id === id);
              if (pr) { setSelectedEvent({ event_id: pr.project_id, name: pr.name, kind: "project", source: pr }); return; }
              setSelectedEvent(null);
            }} data-testid="sm-event-select">
              <option value="">Select event or project…</option>
              {events.length > 0 && (
                <optgroup label="Events">
                  {events.map((e) => <option key={e.event_id} value={e.event_id}>{e.name}</option>)}
                </optgroup>
              )}
              {projects.length > 0 && (
                <optgroup label="External Projects">
                  {projects.map((p) => <option key={p.project_id} value={p.project_id}>✦ {p.name}</option>)}
                </optgroup>
              )}
            </select>
          } />

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard label="Assigned Events" value={events.length} icon={Calendar} color="pink" />
          <StatCard label="Total Posts" value={posts.length} icon={BarChart3} color="orange" />
          <StatCard label="Posted" value={posts.filter((p) => p.posted).length} icon={Check} color="green" />
          <StatCard label="Strategy Notes" value={notes.length} icon={StickyNote} color="indigo" />
        </div>

        <div className="flex gap-2 mb-5 flex-wrap">
          {[
            { key: "calendar", label: "Calendar", Icon: Calendar },
            { key: "notes", label: "Strategy Notes", Icon: StickyNote },
            { key: "links", label: "Saved Links", Icon: LinkIcon },
            ...(user.role === "social_media_manager" || (customRole?.features || []).includes("calendar") ? [{ key: "projects", label: "External Projects", Icon: Sparkles }] : []),
          ].map((v) => (
            <button key={v.key} onClick={() => setView(v.key)} className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 ${view === v.key ? "brand-gradient text-white" : "bg-white border border-slate-200 text-slate-600"}`} data-testid={`sm-view-${v.key}`}>
              <v.Icon className="w-4 h-4" /> {v.label}
            </button>
          ))}
        </div>

        {view === "calendar" && (!selectedEvent ? (
          <div className="dashboard-card p-12 text-center">
            <Sparkles className="w-12 h-12 text-pink-300 mx-auto mb-3" />
            <p className="text-slate-500 mb-4">No event or project selected. Pick one from the dropdown above, or create an External Project from the tab below.</p>
            {projects.length === 0 && (
              <button onClick={() => setView("projects")} className="btn-primary inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Create your first External Project</button>
            )}
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 dashboard-card p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-bold text-lg">{monthName}</h3>
                <div className="flex gap-2">
                  <button onClick={() => setMonthOffset(monthOffset - 1)} className="btn-ghost py-1.5 text-sm">←</button>
                  <button onClick={() => setMonthOffset(0)} className="btn-ghost py-1.5 text-sm">Today</button>
                  <button onClick={() => setMonthOffset(monthOffset + 1)} className="btn-ghost py-1.5 text-sm">→</button>
                </div>
              </div>
              <div className="grid grid-cols-7 gap-1 text-xs font-semibold text-slate-400 mb-2">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="text-center">{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {cells.map((c, i) => (
                  <button key={i} onClick={() => c && c.inWindow && setSelectedDate(c.date)}
                    disabled={!c || !c.inWindow}
                    className={`aspect-square rounded-xl text-sm font-semibold transition-all ${!c ? "" : !c.inWindow ? "bg-slate-50 text-slate-300 opacity-40 cursor-not-allowed" : selectedDate === c.date ? "brand-gradient text-white" : c.posts.length > 0 ? "bg-pink-50 text-pink-700 border border-pink-200" : "bg-slate-50 text-slate-600 hover:bg-slate-100"}`}
                    title={c && !c.inWindow ? "Outside the event's active window (creation → 5 days after end)" : undefined}
                    data-testid={c ? `calendar-day-${c.date}` : undefined}>
                    {c && (
                      <div className="flex flex-col items-center justify-center h-full">
                        <div>{c.day}</div>
                        {c.posts.length > 0 && <div className="text-[10px] mt-0.5">{c.posts.length} post{c.posts.length > 1 ? "s" : ""}</div>}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="dashboard-card p-5">
              <h4 className="font-bold mb-2">{selectedDate || "Pick a day"}</h4>
              {!selectedDate && <p className="text-sm text-slate-400">Click a date on the calendar.</p>}
              {selectedDate && (
                <>
                  <div className="space-y-2 mb-4 max-h-64 overflow-y-auto custom-scrollbar">
                    {dayPosts.length === 0 && <p className="text-sm text-slate-400">No posts scheduled.</p>}
                    {dayPosts.map((p) => (
                      <button key={p.post_id} onClick={() => setOpenPost(p)} className="p-3 rounded-xl border border-slate-100 hover:bg-slate-50 transition w-full text-left block" data-testid={`post-${p.post_id}`}>
                        <div className="flex items-start gap-2">
                          <span onClick={(ev) => { ev.stopPropagation(); togglePosted(p); }} className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 cursor-pointer ${p.posted ? "bg-pink-500 border-pink-500" : "border-slate-300"}`}>{p.posted && <Check className="w-3 h-3 text-white" />}</span>
                          <div className="flex-1 min-w-0">
                            <div className={`font-semibold text-sm ${p.posted ? "line-through text-slate-400" : ""}`}>{p.title}</div>
                            <div className="text-xs text-slate-500 capitalize">{p.platform}{p.description ? ` · ${p.description.slice(0, 40)}${p.description.length > 40 ? "…" : ""}` : ""}</div>
                            {p.posted && p.posted_at && (
                              <div className="text-[10px] text-emerald-700 mt-0.5">Posted {new Date(p.posted_at).toLocaleString()}{p.posted_by_name ? ` by ${p.posted_by_name}` : ""}</div>
                            )}
                          </div>
                          <span onClick={(ev) => { ev.stopPropagation(); delPost(p); }} className="text-rose-500 cursor-pointer"><Trash2 className="w-4 h-4" /></span>
                        </div>
                      </button>
                    ))}
                  </div>
                  <form onSubmit={addPost} className="space-y-2">
                    <input required placeholder="Post title" className="input-pill text-sm py-2" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="post-title" />
                    <textarea rows={4} placeholder="Content idea / script (paste your full script here)" className="input-pill text-sm py-2" value={form.content_idea} onChange={(e) => setForm({ ...form, content_idea: e.target.value })} data-testid="post-content-idea" />
                    <textarea rows={2} placeholder="Description / Caption" className="input-pill text-sm py-2" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="post-description" />
                    <select className="input-pill text-sm py-2" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })}>
                      {PLATFORMS.map((p) => <option key={p}>{p}</option>)}
                    </select>
                    {/* Custom fields */}
                    {(form.custom_fields || []).map((cf, i) => (
                      <div key={i} className="flex gap-1">
                        <input placeholder="Field name" className="input-pill text-xs py-2 flex-1" value={cf.name} onChange={(e) => {
                          const next = [...form.custom_fields]; next[i] = { ...next[i], name: e.target.value }; setForm({ ...form, custom_fields: next });
                        }} />
                        <input placeholder="Content" className="input-pill text-xs py-2 flex-1" value={cf.content} onChange={(e) => {
                          const next = [...form.custom_fields]; next[i] = { ...next[i], content: e.target.value }; setForm({ ...form, custom_fields: next });
                        }} />
                        <button type="button" onClick={() => setForm({ ...form, custom_fields: form.custom_fields.filter((_, j) => j !== i) })} className="text-rose-500 px-1"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    ))}
                    <button type="button" onClick={() => setForm({ ...form, custom_fields: [...(form.custom_fields || []), { name: "", content: "" }] })} className="text-xs text-pink-600 font-bold flex items-center gap-1" data-testid="post-add-custom-field"><Plus className="w-3 h-3" /> Add custom field</button>
                    <button className="btn-primary w-full py-2 text-sm" data-testid="post-submit"><Plus className="w-3 h-3" /> Add post</button>
                  </form>
                </>
              )}
            </div>
          </div>
        ))}

        {view === "notes" && <SMNotes notes={notes} setNotes={setNotes} />}
        {view === "links" && <SMLinks links={links} setLinks={setLinks} />}
        {view === "projects" && (user.role === "social_media_manager" || user.role === "admin" || (customRole?.features || []).includes("calendar")) && <SMProjects />}
      </div>

      {/* Post thread detail modal */}
      {openPost && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setOpenPost(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="post-detail-modal">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-pink-50 to-fuchsia-50">
              <div>
                <h3 className="font-display font-black text-xl">{openPost.title}</h3>
                <div className="text-xs text-slate-500 capitalize mt-0.5">{openPost.platform} · scheduled {openPost.scheduled_date}</div>
              </div>
              <button onClick={() => setOpenPost(null)} className="p-2 hover:bg-white rounded-lg" data-testid="post-detail-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3 p-3 rounded-xl border border-slate-100">
                <button onClick={async () => { await togglePosted(openPost); setOpenPost({ ...openPost, posted: !openPost.posted, posted_at: !openPost.posted ? new Date().toISOString() : null }); }} className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${openPost.posted ? "bg-emerald-500 border-emerald-500" : "border-slate-300"}`} data-testid="post-detail-toggle">
                  {openPost.posted && <Check className="w-4 h-4 text-white" />}
                </button>
                <div className="flex-1">
                  <div className="font-bold text-sm">{openPost.posted ? "Posted ✓" : "Not posted yet"}</div>
                  {openPost.posted && openPost.posted_at && (
                    <div className="text-xs text-emerald-700">{new Date(openPost.posted_at).toLocaleString()}{openPost.posted_by_name ? ` by ${openPost.posted_by_name}` : ""}</div>
                  )}
                </div>
              </div>

              {/* Reschedule to any date within the event window */}
              <div className="p-3 rounded-xl border border-slate-100 bg-slate-50" data-testid="post-detail-move">
                <div className="text-[10px] uppercase font-bold text-slate-500 mb-1.5">Move / reschedule to a different date</div>
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    type="date"
                    defaultValue={openPost.scheduled_date}
                    min={windowRange?.start || undefined}
                    max={windowRange?.end || undefined}
                    onChange={(e) => setOpenPost({ ...openPost, _pendingDate: e.target.value })}
                    className="input-pill py-2 text-sm flex-1 min-w-[10rem]"
                    data-testid="post-detail-move-input"
                  />
                  <button
                    onClick={() => movePost(openPost, openPost._pendingDate || openPost.scheduled_date)}
                    disabled={!openPost._pendingDate || openPost._pendingDate === openPost.scheduled_date}
                    className="btn-primary text-xs py-2 px-3 disabled:opacity-50"
                    data-testid="post-detail-move-btn"
                  >Move</button>
                </div>
                {windowRange && (
                  <div className="text-[10px] text-slate-500 mt-1.5">Allowed window: {windowRange.start} → {windowRange.end}</div>
                )}
              </div>

              {openPost.content_idea && (
                <div>
                  <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">Content Idea / Script</div>
                  <div className="text-sm whitespace-pre-wrap bg-slate-50 rounded-xl p-3 border border-slate-100">{openPost.content_idea}</div>
                </div>
              )}
              {openPost.description && (
                <div>
                  <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">Description / Caption</div>
                  <div className="text-sm whitespace-pre-wrap bg-slate-50 rounded-xl p-3 border border-slate-100">{openPost.description}</div>
                </div>
              )}
              {(openPost.custom_fields || []).length > 0 && (
                <div>
                  <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">Custom Fields</div>
                  <div className="space-y-2">
                    {(openPost.custom_fields || []).map((cf, i) => (
                      <div key={i} className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                        <div className="text-xs font-bold text-slate-700 mb-0.5">{cf.name}</div>
                        <div className="text-sm whitespace-pre-wrap">{cf.content}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {openPost.image_url && (
                <div>
                  <div className="text-[10px] uppercase font-bold text-slate-500 mb-1 flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Attached image</div>
                  <a href={openPost.image_url.startsWith("http") ? openPost.image_url : `${BACKEND_URL}${openPost.image_url}`} target="_blank" rel="noreferrer" className="block">
                    <img src={openPost.image_url.startsWith("http") ? openPost.image_url : `${BACKEND_URL}${openPost.image_url}`} alt="" className="rounded-xl max-h-64 border border-slate-100" />
                  </a>
                </div>
              )}
              {openPost.design_link && (
                <div className="rounded-xl p-3 border-2 border-pink-200 bg-pink-50" data-testid="post-detail-design-link">
                  <div className="text-[10px] uppercase font-bold text-pink-600 mb-1 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Designer submission</div>
                  {openPost.design_thumbnail && (
                    <a href={openPost.design_link} target="_blank" rel="noreferrer" className="block mb-2">
                      <img src={openPost.design_thumbnail} alt="" className="rounded-lg max-h-40 border border-pink-100" onError={(e) => { e.target.style.display = "none"; }} />
                    </a>
                  )}
                  <a href={openPost.design_link} target="_blank" rel="noreferrer" className="text-sm text-pink-700 font-semibold underline break-all inline-flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" /> Open designer link
                  </a>
                  {(openPost.design_added_by_name || openPost.design_added_at) && (
                    <div className="text-[10px] text-slate-500 mt-1">
                      {openPost.design_added_by_name ? `Attached by ${openPost.design_added_by_name}` : ""}
                      {openPost.design_added_at ? ` · ${new Date(openPost.design_added_at).toLocaleString()}` : ""}
                    </div>
                  )}
                  {openPost.design_note && <div className="text-xs text-slate-600 italic mt-1 whitespace-pre-wrap">&ldquo;{openPost.design_note}&rdquo;</div>}
                </div>
              )}
              <div className="text-[10px] text-slate-400 pt-2 border-t border-slate-100">
                Created {openPost.created_at ? new Date(openPost.created_at).toLocaleString() : "—"}
                {openPost.updated_at ? ` · last edited ${new Date(openPost.updated_at).toLocaleString()}` : ""}
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function SMNotes({ notes, setNotes }) {
  const [form, setForm] = useState({ title: "", body: "", color: "amber" });
  const colors = { amber: "bg-amber-50 border-amber-200", pink: "bg-pink-50 border-pink-200", teal: "bg-teal-50 border-teal-200", violet: "bg-violet-50 border-violet-200" };

  const add = async (e) => {
    e.preventDefault();
    const r = await api.post("/sm/notes", form);
    setNotes([r.data, ...notes]);
    setForm({ title: "", body: "", color: "amber" });
  };
  const del = async (n) => { await api.delete(`/sm/notes/${n.note_id}`); setNotes(notes.filter((x) => x.note_id !== n.note_id)); };

  return (
    <div className="grid lg:grid-cols-4 gap-5">
      <form onSubmit={add} className="dashboard-card p-5 space-y-2 h-fit">
        <h4 className="font-bold">New strategy note</h4>
        <input required placeholder="Title" className="input-pill" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="sm-note-title" />
        <textarea required rows={4} placeholder="Notes / strategy" className="input-pill" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
        <select className="input-pill" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })}>
          {Object.keys(colors).map((c) => <option key={c}>{c}</option>)}
        </select>
        <button className="btn-primary w-full py-2.5" data-testid="sm-note-add"><Plus className="w-3 h-3" /> Add note</button>
      </form>
      <div className="lg:col-span-3 grid sm:grid-cols-2 gap-3">
        {notes.length === 0 && <div className="col-span-full dashboard-card p-12 text-center text-slate-400">No notes yet.</div>}
        {notes.map((n) => (
          <motion.div whileHover={{ y: -2 }} key={n.note_id} className={`p-5 rounded-2xl border ${colors[n.color] || colors.amber}`} data-testid={`sm-note-${n.note_id}`}>
            <div className="flex justify-between items-start">
              <div className="font-display font-bold text-lg">{n.title}</div>
              <button onClick={() => del(n)} className="text-rose-500"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="text-sm text-slate-700 whitespace-pre-wrap mt-2">{n.body}</div>
            <div className="text-xs text-slate-500 mt-3">{n.updated_at?.slice(0, 10)}</div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function SMLinks({ links, setLinks }) {
  const [form, setForm] = useState({ url: "", title: "", tag: "inspiration", notes: "" });
  const tags = ["inspiration", "reel-idea", "reference", "competitor", "tool"];

  const add = async (e) => {
    e.preventDefault();
    const r = await api.post("/sm/links", form);
    setLinks([r.data, ...links]);
    setForm({ url: "", title: "", tag: "inspiration", notes: "" });
  };
  const del = async (l) => { await api.delete(`/sm/links/${l.link_id}`); setLinks(links.filter((x) => x.link_id !== l.link_id)); };

  return (
    <div className="grid lg:grid-cols-4 gap-5">
      <form onSubmit={add} className="dashboard-card p-5 space-y-2 h-fit">
        <h4 className="font-bold">Save a link</h4>
        <input required type="url" placeholder="https://…" className="input-pill" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="sm-link-url" />
        <input placeholder="Title" className="input-pill" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <select className="input-pill" value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })}>{tags.map((t) => <option key={t}>{t}</option>)}</select>
        <textarea rows={2} placeholder="Notes" className="input-pill" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        <button className="btn-primary w-full py-2.5" data-testid="sm-link-add"><Plus className="w-3 h-3" /> Save link</button>
      </form>
      <div className="lg:col-span-3 space-y-2">
        {links.length === 0 && <div className="dashboard-card p-12 text-center text-slate-400">No saved links yet.</div>}
        {links.map((l) => (
          <div key={l.link_id} className="dashboard-card p-4 flex items-center gap-3" data-testid={`sm-link-${l.link_id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-xs px-2 py-0.5 rounded-full bg-pink-100 text-pink-700">{l.tag}</span>
                <a href={l.url} target="_blank" rel="noreferrer" className="font-semibold text-orange-600 truncate">{l.title || l.url}</a>
              </div>
              <div className="text-xs text-slate-500 truncate">{l.url}</div>
              {l.notes && <div className="text-xs text-slate-600 mt-1">{l.notes}</div>}
            </div>
            <a href={l.url} target="_blank" rel="noreferrer" className="text-slate-500"><ExternalLink className="w-4 h-4" /></a>
            <button onClick={() => del(l)} className="text-rose-500"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function SMProjects() {
  const [projects, setProjects] = useState([]);
  const [execs, setExecs] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", client: "", description: "", sm_executive_id: "", start_date: "", end_date: "" });
  const submit = useSubmitting();

  const load = async () => {
    const [p, u] = await Promise.all([
      api.get("/sm-projects"),
      api.get("/users?role=social_media_executive").catch(() => ({ data: [] })),
    ]);
    setProjects(p.data); setExecs(u.data);
  };
  useEffect(() => { load(); }, []);

  const create = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name required");
    return submit.run(async () => {
      try {
        const r = await api.post("/sm-projects", form);
        const wasAssignedToAdmin = r.data.assigned_to_admin;
        toast.success(wasAssignedToAdmin
          ? "Project created — managed by Admin by default. You can keep working on the calendar, notes & strategy."
          : "Project created and assigned to the chosen SM Executive."
        );
        setShowNew(false);
        setForm({ name: "", client: "", description: "", sm_executive_id: "", start_date: "", end_date: "" });
        load();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed");
      }
    });
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete project "${p.name}"?`)) return;
    await api.delete(`/sm-projects/${p.project_id}`);
    load();
  };

  return (
    <div data-testid="sm-projects">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display font-bold text-lg">External Projects</h3>
          <p className="text-xs text-slate-500">Personal SM client work — NOT visible to Regional PM unless you assign an SM Executive.</p>
        </div>
        <button onClick={() => setShowNew(true)} className="btn-primary py-2 px-4 text-sm" data-testid="sm-project-new">+ New project</button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {projects.length === 0 && <div className="dashboard-card p-8 text-center text-slate-400 col-span-full">No external projects yet.</div>}
        {projects.map((p) => (
          <div key={p.project_id} className="dashboard-card p-5">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <div className="font-display font-bold text-lg">{p.name}</div>
                {p.client && <div className="text-xs text-pink-700">{p.client}</div>}
              </div>
              <button onClick={() => remove(p)} className="text-rose-500"><Trash2 className="w-4 h-4" /></button>
            </div>
            {p.description && <p className="text-sm text-slate-600 line-clamp-3">{p.description}</p>}
            <div className="mt-3 flex items-center justify-between text-xs">
              <span className={`role-pill ${p.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100"}`}>{p.status}</span>
              {p.sm_executive_id
                ? <span className="text-slate-500">assigned ✓</span>
                : <span className="inline-flex items-center gap-1 text-indigo-700 font-bold"><Shield className="w-3 h-3" /> Managed by Admin</span>}
            </div>
            {(p.start_date || p.end_date) && <div className="text-[11px] text-slate-400 mt-1">{p.start_date} → {p.end_date}</div>}
          </div>
        ))}
      </div>

      {showNew && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <form onSubmit={create} className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto space-y-3 relative" data-testid="sm-project-modal">
            <ModalCloseBtn onClose={() => setShowNew(false)} />
            <h3 className="font-display font-black text-2xl">New external project</h3>
            <input required placeholder="Project name" className="input-pill" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sm-project-name" />
            <input placeholder="Client (optional)" className="input-pill" value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} />
            <textarea rows={3} placeholder="Description" className="input-pill" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <div className="grid grid-cols-2 gap-2">
              <input type="date" className="input-pill" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              <input type="date" className="input-pill" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 block mb-1">Assign SM Executive (optional)</label>
              <select className="input-pill" value={form.sm_executive_id} onChange={(e) => setForm({ ...form, sm_executive_id: e.target.value })}>
                <option value="">— Leave unassigned → Managed by Admin —</option>
                {execs.map((u) => <option key={u.user_id} value={u.user_id}>{u.name} · {u.city || "—"}</option>)}
              </select>
              <div className="text-[11px] text-indigo-600 mt-1 flex items-center gap-1"><Shield className="w-3 h-3" /> Default: project goes to Admin. You can still own the calendar, notes & strategy.</div>
            </div>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={() => setShowNew(false)} disabled={submit.submitting} className="btn-ghost flex-1 py-3 disabled:opacity-50">Cancel</button>
              <button type="submit" disabled={submit.submitting} className="btn-primary flex-1 py-3 disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="sm-project-submit">
                {submit.submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating…</> : "Create"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

